import { useState } from 'react';
import { Download } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Spinner } from '@/components/ui/spinner';
import { PageHeader } from '@/layouts/AppLayout';
import { cn, formatCurrency, formatDateOnly, formatInt } from '@/lib/utils';

// ── CSV export ────────────────────────────────────────────────────────────────

function exportCsv(rows: Record<string, unknown>[], filename: string) {
  if (rows.length === 0) return;
  const headers = Object.keys(rows[0]);
  const lines = [
    headers.join(','),
    ...rows.map((r) =>
      headers.map((h) => JSON.stringify(r[h] ?? '')).join(','),
    ),
  ];
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// ── Tabs ──────────────────────────────────────────────────────────────────────

type Tab = 'by-hotel' | 'by-client' | 'revenue-trend' | 'commissions' | 'cancellations';

const TABS: { id: Tab; label: string }[] = [
  { id: 'by-hotel', label: 'By Hotel' },
  { id: 'by-client', label: 'By Client' },
  { id: 'revenue-trend', label: 'Revenue Trend' },
  { id: 'commissions', label: 'Commission Settlement' },
  { id: 'cancellations', label: 'Cancellations' },
];

// ── Date filter ───────────────────────────────────────────────────────────────

function DateFilter({
  from,
  to,
  onChange,
}: {
  from: string;
  to: string;
  onChange: (from: string, to: string) => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-3 py-4">
      <span className="text-sm text-neutral-500">Check-in from</span>
      <input
        type="date"
        value={from}
        onChange={(e) => onChange(e.target.value, to)}
        className="rounded-md border border-neutral-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-tangerine-500"
      />
      <span className="text-sm text-neutral-500">to</span>
      <input
        type="date"
        value={to}
        onChange={(e) => onChange(from, e.target.value)}
        className="rounded-md border border-neutral-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-tangerine-500"
      />
      {(from || to) && (
        <button
          onClick={() => onChange('', '')}
          className="text-xs text-neutral-500 hover:text-neutral-700"
        >
          Clear
        </button>
      )}
    </div>
  );
}

// ── Shared primitives ─────────────────────────────────────────────────────────

function SummaryCard({
  label,
  value,
  sub,
}: {
  label: string;
  value: string;
  sub?: string;
}) {
  return (
    <Card>
      <CardContent className="pb-4 pt-5">
        <p className="text-sm font-medium text-neutral-500">{label}</p>
        <p className="mt-1 text-2xl font-bold text-neutral-900">{value}</p>
        {sub && <p className="mt-0.5 text-xs text-neutral-400">{sub}</p>}
      </CardContent>
    </Card>
  );
}

function Th({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <th
      className={cn(
        'bg-neutral-50 px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500',
        className,
      )}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <td
      className={cn(
        'border-t border-neutral-100 px-4 py-3 text-neutral-700',
        className,
      )}
    >
      {children}
    </td>
  );
}

function CsvButton({
  onClick,
  disabled,
}: {
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <Button variant="outline" size="sm" onClick={onClick} disabled={disabled}>
      <Download className="mr-1.5 h-3.5 w-3.5" />
      Export CSV
    </Button>
  );
}

function LoadingState() {
  return (
    <div className="flex justify-center py-16">
      <Spinner className="h-6 w-6" />
    </div>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <p className="py-16 text-center text-sm text-neutral-400">{message}</p>
  );
}

// ── 1. Bookings by Hotel ──────────────────────────────────────────────────────

interface HotelRow {
  hotelId: string;
  hotelName: string;
  city: string;
  bookingCount: number;
  totalRoomNights: number;
  totalRevenue: number;
  avgBookingValue: number;
}
interface HotelReport {
  data: HotelRow[];
  summary: { totalBookings: number; totalRevenue: number };
}

function BookingsByHotelReport({ from, to }: { from: string; to: string }) {
  const qs = buildQs({ from, to });
  const { data, isPending } = useQuery<HotelReport>({
    queryKey: ['reports', 'by-hotel', from, to],
    queryFn: () => api.get<HotelReport>(`/reports/bookings-by-hotel${qs}`),
  });

  if (isPending) return <LoadingState />;
  if (!data || data.data.length === 0)
    return <EmptyState message="No bookings found for the selected period." />;

  const csvRows = data.data.map((r) => ({
    Hotel: r.hotelName,
    City: r.city,
    Bookings: r.bookingCount,
    'Room Nights': r.totalRoomNights,
    'Revenue (INR)': r.totalRevenue,
    'Avg Value (INR)': r.avgBookingValue,
  }));

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <SummaryCard label="Hotels" value={formatInt(data.data.length)} />
        <SummaryCard
          label="Total Bookings"
          value={formatInt(data.summary.totalBookings)}
        />
        <SummaryCard
          label="Total Revenue"
          value={formatCurrency(data.summary.totalRevenue)}
        />
        <SummaryCard
          label="Avg per Booking"
          value={
            data.summary.totalBookings > 0
              ? formatCurrency(
                  Math.round(
                    data.summary.totalRevenue / data.summary.totalBookings,
                  ),
                )
              : '—'
          }
        />
      </div>

      <div className="flex justify-end">
        <CsvButton
          onClick={() => exportCsv(csvRows as Record<string, unknown>[], 'bookings-by-hotel.csv')}
        />
      </div>

      <div className="overflow-x-auto rounded-lg border border-neutral-200">
        <table className="w-full text-sm">
          <thead>
            <tr>
              <Th>Hotel</Th>
              <Th>City</Th>
              <Th className="text-right">Bookings</Th>
              <Th className="text-right">Room Nights</Th>
              <Th className="text-right">Revenue</Th>
              <Th className="text-right">Avg Value</Th>
            </tr>
          </thead>
          <tbody>
            {data.data.map((r) => (
              <tr key={r.hotelId} className="hover:bg-neutral-50">
                <Td className="font-medium">{r.hotelName}</Td>
                <Td>{r.city}</Td>
                <Td className="text-right">{formatInt(r.bookingCount)}</Td>
                <Td className="text-right">{formatInt(r.totalRoomNights)}</Td>
                <Td className="text-right font-medium">
                  {formatCurrency(r.totalRevenue)}
                </Td>
                <Td className="text-right">
                  {formatCurrency(r.avgBookingValue)}
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── 2. Bookings by Client ─────────────────────────────────────────────────────

interface ClientRow {
  clientId: string;
  clientName: string;
  clientType: string;
  bookingCount: number;
  totalRoomNights: number;
  totalRevenue: number;
  avgBookingValue: number;
}
interface ClientReport {
  data: ClientRow[];
  summary: { totalBookings: number; totalRevenue: number };
}

const CLIENT_TYPE_LABELS: Record<string, string> = {
  travel_agent: 'Travel Agent',
  b2c: 'B2C',
  corporate: 'Corporate',
};

function BookingsByClientReport({ from, to }: { from: string; to: string }) {
  const qs = buildQs({ from, to });
  const { data, isPending } = useQuery<ClientReport>({
    queryKey: ['reports', 'by-client', from, to],
    queryFn: () => api.get<ClientReport>(`/reports/bookings-by-client${qs}`),
  });

  if (isPending) return <LoadingState />;
  if (!data || data.data.length === 0)
    return <EmptyState message="No bookings found for the selected period." />;

  const csvRows = data.data.map((r) => ({
    Client: r.clientName,
    Type: CLIENT_TYPE_LABELS[r.clientType] ?? r.clientType,
    Bookings: r.bookingCount,
    'Room Nights': r.totalRoomNights,
    'Revenue (INR)': r.totalRevenue,
    'Avg Value (INR)': r.avgBookingValue,
  }));

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <SummaryCard label="Clients" value={formatInt(data.data.length)} />
        <SummaryCard
          label="Total Bookings"
          value={formatInt(data.summary.totalBookings)}
        />
        <SummaryCard
          label="Total Revenue"
          value={formatCurrency(data.summary.totalRevenue)}
        />
        <SummaryCard
          label="Avg per Booking"
          value={
            data.summary.totalBookings > 0
              ? formatCurrency(
                  Math.round(
                    data.summary.totalRevenue / data.summary.totalBookings,
                  ),
                )
              : '—'
          }
        />
      </div>

      <div className="flex justify-end">
        <CsvButton
          onClick={() => exportCsv(csvRows as Record<string, unknown>[], 'bookings-by-client.csv')}
        />
      </div>

      <div className="overflow-x-auto rounded-lg border border-neutral-200">
        <table className="w-full text-sm">
          <thead>
            <tr>
              <Th>Client</Th>
              <Th>Type</Th>
              <Th className="text-right">Bookings</Th>
              <Th className="text-right">Room Nights</Th>
              <Th className="text-right">Revenue</Th>
              <Th className="text-right">Avg Value</Th>
            </tr>
          </thead>
          <tbody>
            {data.data.map((r) => (
              <tr key={r.clientId} className="hover:bg-neutral-50">
                <Td className="font-medium">{r.clientName}</Td>
                <Td>
                  <span className="rounded-full bg-neutral-100 px-2 py-0.5 text-xs font-medium text-neutral-600">
                    {CLIENT_TYPE_LABELS[r.clientType] ?? r.clientType}
                  </span>
                </Td>
                <Td className="text-right">{formatInt(r.bookingCount)}</Td>
                <Td className="text-right">{formatInt(r.totalRoomNights)}</Td>
                <Td className="text-right font-medium">
                  {formatCurrency(r.totalRevenue)}
                </Td>
                <Td className="text-right">
                  {formatCurrency(r.avgBookingValue)}
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── 3. Revenue Trend (Recharts bar chart) ─────────────────────────────────────

interface TrendRow {
  month: string;
  label: string;
  revenue: number;
  bookingCount: number;
}
interface TrendReport {
  data: TrendRow[];
}

// Sequential tangerine palette (section 10.1.1)
const TAN_500 = '#F57C1F';
const TAN_600 = '#D65F0E';

function RevenueTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: { payload: TrendRow }[];
  label?: string;
}) {
  if (!active || !payload?.length) return null;
  const { revenue, bookingCount } = payload[0].payload;
  return (
    <div className="rounded-lg border border-neutral-200 bg-white px-3 py-2 shadow-sm">
      <p className="text-xs font-medium text-neutral-500">{label}</p>
      <p className="text-sm font-bold text-neutral-900">
        {formatCurrency(revenue)}
      </p>
      <p className="text-xs text-neutral-400">{formatInt(bookingCount)} bookings</p>
    </div>
  );
}

function RevenueTrendReport() {
  const { data, isPending } = useQuery<TrendReport>({
    queryKey: ['reports', 'revenue-trend'],
    queryFn: () => api.get<TrendReport>('/reports/revenue-trend'),
  });

  if (isPending) return <LoadingState />;
  if (!data) return null;

  const totalRevenue = data.data.reduce((s, r) => s + r.revenue, 0);
  const totalBookings = data.data.reduce((s, r) => s + r.bookingCount, 0);
  const peakMonth = data.data.reduce(
    (best, r) => (r.revenue > best.revenue ? r : best),
    data.data[0],
  );

  const csvRows = data.data.map((r) => ({
    Month: r.label,
    'Revenue (INR)': r.revenue,
    Bookings: r.bookingCount,
  }));

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
        <SummaryCard
          label="12-Month Revenue"
          value={formatCurrency(totalRevenue)}
        />
        <SummaryCard
          label="Total Bookings"
          value={formatInt(totalBookings)}
        />
        <SummaryCard
          label="Peak Month"
          value={peakMonth?.label ?? '—'}
          sub={
            peakMonth && peakMonth.revenue > 0
              ? formatCurrency(peakMonth.revenue)
              : undefined
          }
        />
      </div>

      <div className="flex justify-end">
        <CsvButton
          onClick={() => exportCsv(csvRows as Record<string, unknown>[], 'revenue-trend.csv')}
        />
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold">
            Monthly Revenue — Last 12 Months
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart
              data={data.data}
              margin={{ top: 4, right: 8, bottom: 0, left: 8 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="#E7E6E2"
                vertical={false}
              />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 11, fill: '#73726C' }}
                tickLine={false}
                axisLine={false}
              />
              <YAxis
                tickFormatter={(v: number) =>
                  v >= 1_00_000
                    ? `₹${(v / 1_00_000).toFixed(1)}L`
                    : v >= 1_000
                      ? `₹${(v / 1_000).toFixed(0)}K`
                      : `₹${v}`
                }
                tick={{ fontSize: 11, fill: '#73726C' }}
                tickLine={false}
                axisLine={false}
                width={64}
              />
              <Tooltip
                content={
                  <RevenueTooltip />
                }
                cursor={{ fill: '#FFF4EB' }}
              />
              <Bar
                dataKey="revenue"
                fill={TAN_500}
                activeBar={{ fill: TAN_600 }}
                radius={[4, 4, 0, 0]}
                maxBarSize={48}
              />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <div className="overflow-x-auto rounded-lg border border-neutral-200">
        <table className="w-full text-sm">
          <thead>
            <tr>
              <Th>Month</Th>
              <Th className="text-right">Revenue</Th>
              <Th className="text-right">Bookings</Th>
              <Th className="text-right">Avg per Booking</Th>
            </tr>
          </thead>
          <tbody>
            {[...data.data].reverse().map((r) => (
              <tr key={r.month} className="hover:bg-neutral-50">
                <Td className="font-medium">{r.label}</Td>
                <Td className="text-right">
                  {r.revenue > 0 ? formatCurrency(r.revenue) : '—'}
                </Td>
                <Td className="text-right">
                  {r.bookingCount > 0 ? formatInt(r.bookingCount) : '—'}
                </Td>
                <Td className="text-right">
                  {r.bookingCount > 0
                    ? formatCurrency(Math.round(r.revenue / r.bookingCount))
                    : '—'}
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── 4. Commission Settlement ──────────────────────────────────────────────────

interface CommBucket {
  count: number;
  amount: number;
}
interface CommissionRow {
  hotelId: string;
  hotelName: string;
  pending: CommBucket;
  invoiced: CommBucket;
  settled: CommBucket;
  writtenOff: CommBucket;
  total: CommBucket;
}
interface CommissionReport {
  data: CommissionRow[];
}

function CommissionSettlementReport() {
  const { data, isPending } = useQuery<CommissionReport>({
    queryKey: ['reports', 'commission-settlement'],
    queryFn: () =>
      api.get<CommissionReport>('/reports/commission-settlement'),
  });

  if (isPending) return <LoadingState />;
  if (!data || data.data.length === 0)
    return <EmptyState message="No commission data found." />;

  const totalAll = data.data.reduce((s, r) => s + r.total.amount, 0);
  const totalUnsettled = data.data.reduce(
    (s, r) => s + r.pending.amount + r.invoiced.amount,
    0,
  );
  const totalSettled = data.data.reduce((s, r) => s + r.settled.amount, 0);

  const csvRows = data.data.map((r) => ({
    Hotel: r.hotelName,
    'Pending Count': r.pending.count,
    'Pending (INR)': r.pending.amount,
    'Invoiced Count': r.invoiced.count,
    'Invoiced (INR)': r.invoiced.amount,
    'Settled Count': r.settled.count,
    'Settled (INR)': r.settled.amount,
    'Written-off Count': r.writtenOff.count,
    'Written-off (INR)': r.writtenOff.amount,
    'Total (INR)': r.total.amount,
  }));

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
        <SummaryCard
          label="Total Commission"
          value={formatCurrency(totalAll)}
        />
        <SummaryCard
          label="Pending / Invoiced"
          value={formatCurrency(totalUnsettled)}
        />
        <SummaryCard
          label="Settled"
          value={formatCurrency(totalSettled)}
        />
      </div>

      <div className="flex justify-end">
        <CsvButton
          onClick={() => exportCsv(csvRows as Record<string, unknown>[], 'commission-settlement.csv')}
        />
      </div>

      <div className="overflow-x-auto rounded-lg border border-neutral-200">
        <table className="w-full text-sm">
          <thead>
            <tr>
              <Th>Hotel</Th>
              <Th className="text-right">Pending</Th>
              <Th className="text-right">Invoiced</Th>
              <Th className="text-right">Settled</Th>
              <Th className="text-right">Written-off</Th>
              <Th className="text-right">Total</Th>
            </tr>
          </thead>
          <tbody>
            {data.data.map((r) => (
              <tr key={r.hotelId} className="hover:bg-neutral-50">
                <Td className="font-medium">{r.hotelName}</Td>
                <Td className="text-right">
                  {r.pending.count > 0 ? (
                    <>
                      <span className="font-medium text-warning-700">
                        {formatCurrency(r.pending.amount)}
                      </span>
                      <br />
                      <span className="text-xs text-neutral-400">
                        {r.pending.count} comm.
                      </span>
                    </>
                  ) : (
                    '—'
                  )}
                </Td>
                <Td className="text-right">
                  {r.invoiced.count > 0 ? (
                    <>
                      <span className="font-medium text-info-700">
                        {formatCurrency(r.invoiced.amount)}
                      </span>
                      <br />
                      <span className="text-xs text-neutral-400">
                        {r.invoiced.count} comm.
                      </span>
                    </>
                  ) : (
                    '—'
                  )}
                </Td>
                <Td className="text-right">
                  {r.settled.count > 0 ? (
                    <>
                      <span className="font-medium text-success-700">
                        {formatCurrency(r.settled.amount)}
                      </span>
                      <br />
                      <span className="text-xs text-neutral-400">
                        {r.settled.count} comm.
                      </span>
                    </>
                  ) : (
                    '—'
                  )}
                </Td>
                <Td className="text-right">
                  {r.writtenOff.count > 0 ? (
                    <>
                      <span className="font-medium text-neutral-500">
                        {formatCurrency(r.writtenOff.amount)}
                      </span>
                      <br />
                      <span className="text-xs text-neutral-400">
                        {r.writtenOff.count} comm.
                      </span>
                    </>
                  ) : (
                    '—'
                  )}
                </Td>
                <Td className="text-right font-semibold">
                  {formatCurrency(r.total.amount)}
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── 5. Cancellations ──────────────────────────────────────────────────────────

interface CancellationRow {
  id: string;
  bookingCode: string;
  guestName: string;
  checkIn: string;
  checkOut: string;
  amount: number;
  cancellationReason: string | null;
  updatedAt: string | null;
  hotel: { name: string; city: string };
  client: { name: string };
}
interface CancellationReport {
  data: CancellationRow[];
  meta: { total: number; cancelledRevenue: number };
}

function CancellationsReport({ from, to }: { from: string; to: string }) {
  const qs = buildQs({ from, to });
  const { data, isPending } = useQuery<CancellationReport>({
    queryKey: ['reports', 'cancellations', from, to],
    queryFn: () =>
      api.get<CancellationReport>(`/reports/cancellations${qs}`),
  });

  if (isPending) return <LoadingState />;
  if (!data || data.data.length === 0)
    return (
      <EmptyState message="No cancellations found for the selected period." />
    );

  const csvRows = data.data.map((r) => ({
    'Booking Code': r.bookingCode,
    Guest: r.guestName,
    Hotel: r.hotel.name,
    City: r.hotel.city,
    Client: r.client.name,
    'Check-in': r.checkIn?.slice(0, 10) ?? '',
    'Check-out': r.checkOut?.slice(0, 10) ?? '',
    'Amount (INR)': r.amount,
    Reason: r.cancellationReason ?? '',
    'Cancelled On': r.updatedAt?.slice(0, 10) ?? '',
  }));

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <SummaryCard
          label="Total Cancellations"
          value={formatInt(data.meta.total)}
        />
        <SummaryCard
          label="Cancelled Revenue"
          value={formatCurrency(data.meta.cancelledRevenue)}
        />
      </div>

      <div className="flex justify-end">
        <CsvButton
          onClick={() => exportCsv(csvRows as Record<string, unknown>[], 'cancellations.csv')}
        />
      </div>

      <div className="overflow-x-auto rounded-lg border border-neutral-200">
        <table className="w-full text-sm">
          <thead>
            <tr>
              <Th>Booking</Th>
              <Th>Guest</Th>
              <Th>Hotel</Th>
              <Th>Client</Th>
              <Th>Check-in</Th>
              <Th>Check-out</Th>
              <Th className="text-right">Amount</Th>
              <Th>Reason</Th>
            </tr>
          </thead>
          <tbody>
            {data.data.map((r) => (
              <tr key={r.id} className="hover:bg-neutral-50">
                <Td>
                  <span className="font-mono text-xs font-medium text-neutral-500">
                    {r.bookingCode}
                  </span>
                </Td>
                <Td className="font-medium">{r.guestName}</Td>
                <Td>
                  {r.hotel.name},{' '}
                  <span className="text-neutral-500">{r.hotel.city}</span>
                </Td>
                <Td>{r.client.name}</Td>
                <Td>{formatDateOnly(r.checkIn)}</Td>
                <Td>{formatDateOnly(r.checkOut)}</Td>
                <Td className="text-right">{formatCurrency(r.amount)}</Td>
                <Td className="max-w-[14rem] truncate text-neutral-500">
                  {r.cancellationReason ?? '—'}
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function buildQs(params: Record<string, string>): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v) p.set(k, v);
  }
  const s = p.toString();
  return s ? `?${s}` : '';
}

// ── Page ──────────────────────────────────────────────────────────────────────

export function ReportsPage() {
  const [tab, setTab] = useState<Tab>('by-hotel');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const handleDateChange = (newFrom: string, newTo: string) => {
    setFrom(newFrom);
    setTo(newTo);
  };

  return (
    <div>
      <PageHeader
        title="Reports"
        description="Analyse booking data and export to CSV."
      />

      <div className="px-6 py-4">
        {/* Tab bar */}
        <div className="border-b border-neutral-200">
          <nav className="-mb-px flex gap-0.5" aria-label="Report tabs">
            {TABS.map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={cn(
                  'whitespace-nowrap border-b-2 px-4 py-2.5 text-sm font-medium transition-colors',
                  tab === t.id
                    ? 'border-tangerine-500 text-tangerine-600'
                    : 'border-transparent text-neutral-500 hover:border-neutral-300 hover:text-neutral-700',
                )}
              >
                {t.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Date filter — not shown for revenue trend (always last 12 months) */}
        {tab !== 'revenue-trend' && tab !== 'commissions' && (
          <DateFilter from={from} to={to} onChange={handleDateChange} />
        )}

        {/* Report panels */}
        <div className={tab !== 'revenue-trend' && tab !== 'commissions' ? 'mt-0' : 'mt-4'}>
          {tab === 'by-hotel' && (
            <BookingsByHotelReport from={from} to={to} />
          )}
          {tab === 'by-client' && (
            <BookingsByClientReport from={from} to={to} />
          )}
          {tab === 'revenue-trend' && <RevenueTrendReport />}
          {tab === 'commissions' && <CommissionSettlementReport />}
          {tab === 'cancellations' && (
            <CancellationsReport from={from} to={to} />
          )}
        </div>
      </div>
    </div>
  );
}
